export type AudienceSurveyRef = {
  id: string;
  writeToken: string;
};

export type AudienceSurveyAnswers = {
  audience: "kid_youth" | "grown_up" | null;
  setting: "school" | "home" | "somewhere_else" | "school_or_organization" | "at_home" | null;
  role:
    | "teacher"
    | "admin"
    | "specialist"
    | "other"
    | "parent_or_caregiver"
    | "homeschool_parent"
    | "just_playing"
    | null;
  feedback: string;
  score: number | null;
};

type SurveyStep = "audience" | "feedback" | "rate" | "thankyou";

async function postSurvey(body: Record<string, unknown>) {
  const response = await fetch("/api/nps/survey", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const payload = (await response.json().catch(() => null)) as {
    data?: { id?: string; write_token?: string };
    error?: string;
  } | null;
  if (!response.ok) {
    throw new Error(payload?.error || "Failed to save survey.");
  }
  return payload?.data;
}

export async function startAudienceSurveyResponse(input: {
  pagePath: string;
  gameSlug: string;
  gameId: string | null;
}): Promise<AudienceSurveyRef> {
  const data = await postSurvey({
    action: "start",
    page_path: input.pagePath,
    game_slug: input.gameSlug,
    game_id: input.gameId,
  });
  if (typeof data?.id !== "string" || typeof data.write_token !== "string") {
    throw new Error("Failed to start survey.");
  }
  return { id: data.id, writeToken: data.write_token };
}

export async function saveAudienceSurveyProgress(
  ref: AudienceSurveyRef,
  input: AudienceSurveyAnswers & { lastStep: Exclude<SurveyStep, "thankyou"> }
): Promise<void> {
  await postSurvey({
    action: "save",
    id: ref.id,
    write_token: ref.writeToken,
    last_step: input.lastStep,
    audience: input.audience,
    setting: input.setting,
    role: input.role,
    feedback: input.feedback,
    score: input.score,
    complete: false,
  });
}

export async function submitNpsResponse(
  ref: AudienceSurveyRef,
  input: AudienceSurveyAnswers & { score: number }
): Promise<{ id: string }> {
  const data = await postSurvey({
    action: "save",
    id: ref.id,
    write_token: ref.writeToken,
    last_step: "thankyou",
    audience: input.audience,
    setting: input.setting,
    role: input.role,
    feedback: input.feedback,
    score: input.score,
    complete: true,
  });
  const id = data?.id;
  if (typeof id !== "string" || !id) {
    throw new Error("Failed to save survey.");
  }
  return { id };
}
